/**
 * Hybrid RSA + AES Encryption Utility
 * ------------------------------------
 * - Encrypts large JSON or Base64 data safely for backend
 * - Compatible with backend using AES-256-CFB + RSA-OAEP(SHA256)
 * - Uses node-forge under the hood
 */

import forge from "node-forge";

// 🔐 Load keys from environment variables (Vite)
const backendPublicKeyPem = import.meta.env.VITE_BACKEND_PUBLIC_KEY;
const frontendPrivateKeyPem = import.meta.env.VITE_FRONTEND_PRIVATE_KEY;

/**
 * Encrypt data (string or object) for backend
 * using AES-256-CFB and RSA-OAEP(SHA256)
 * ------------------------------------------
 * @param {Object|string} data - data to encrypt
 * @returns {Object} { encrypted_key, iv, data }
 */
export function encryptForBackend(data) {
  try {
    // Step 1️⃣ - Convert input to JSON string
    const jsonString = typeof data === "string" ? data : JSON.stringify(data);

    // Step 2️⃣ - Generate AES key and IV
    const aesKey = forge.random.getBytesSync(32); // 256-bit AES key
    const iv = forge.random.getBytesSync(16); // 128-bit IV

    // Step 3️⃣ - Encrypt the data with AES-CFB
    const cipher = forge.cipher.createCipher("AES-CFB", aesKey);
    cipher.start({ iv });
    cipher.update(forge.util.createBuffer(jsonString, "utf8"));
    cipher.finish();
    const encryptedData = cipher.output.getBytes();

    // Step 4️⃣ - Encrypt AES key with backend's RSA public key
    const publicKey = forge.pki.publicKeyFromPem(backendPublicKeyPem);
    const encryptedAesKey = publicKey.encrypt(aesKey, "RSA-OAEP", {
      md: forge.md.sha256.create(),
      mgf1: forge.mgf.mgf1.create(forge.md.sha256.create()),
    });

    // Step 5️⃣ - Encode all components in Base64 for transport
    const payload = {
      encrypted_key: forge.util.encode64(encryptedAesKey),
      iv: forge.util.encode64(iv),
      data: forge.util.encode64(encryptedData),
    };

    return payload;
  } catch (err) {
    console.error("❌ Error encrypting for backend:", err);
    return null;
  }
}

/**
 * Decrypt payload received from backend
 * (which uses AES-256-CFB + RSA-OAEP-SHA256)
 * ------------------------------------------
 * @param {Object} payload - { encrypted_key, iv, data }
 * @returns {Object|string|null} decrypted object or string
 */
export function decryptFromBackend(payload) {
  try {
    if (!payload || !payload.encrypted_key || !payload.iv || !payload.data) {
      console.error("❌ Invalid payload structure for decryption.");
      return null;
    }

    // Step 1️⃣ - Import private RSA key
    const privateKey = forge.pki.privateKeyFromPem(frontendPrivateKeyPem);

    // Step 2️⃣ - Decode and decrypt AES key
    const encryptedKeyBytes = forge.util.decode64(payload.encrypted_key);
    const aesKey = privateKey.decrypt(encryptedKeyBytes, "RSA-OAEP", {
      md: forge.md.sha256.create(),
      mgf1: forge.mgf.mgf1.create(forge.md.sha256.create()),
    });

    // Step 3️⃣ - Decode Base64 IV and data
    const iv = forge.util.decode64(payload.iv);
    const encryptedDataBytes = forge.util.decode64(payload.data);

    // Step 4️⃣ - AES decryption
    const decipher = forge.cipher.createDecipher("AES-CFB", aesKey);
    decipher.start({ iv });
    decipher.update(forge.util.createBuffer(encryptedDataBytes));
    decipher.finish();

    // Step 5️⃣ - Return parsed JSON or raw text
    const decryptedString = decipher.output.toString("utf8");
    try {
      return JSON.parse(decryptedString);
    } catch {
      return decryptedString;
    }
  } catch (err) {
    console.error("❌ Error decrypting from backend:", err);
    return null;
  }
}

/**
 * Combined helper — Encrypt → Send → Decrypt
 * ------------------------------------------
 * Automatically encrypts the request payload,
 * sends via provided function, and decrypts the backend response.
 *
 * @param {Object|string} message - data to send
 * @param {Function} sendRequest - async function that sends to backend
 * @returns {Promise<Object|string|null>} decrypted response
 */
export async function sendSecureMessage(message, sendRequest) {
  try {
    const encryptedPayload = encryptForBackend(message);
    if (!encryptedPayload) throw new Error("Encryption failed.");

    // Send encrypted data to backend
    const encryptedResponse = await sendRequest(encryptedPayload);
    if (!encryptedResponse) throw new Error("No response from backend.");

    // Decrypt backend response
    const decryptedResponse = decryptFromBackend(encryptedResponse);
    return decryptedResponse;
  } catch (err) {
    console.error("❌ Secure message exchange failed:", err);
    throw err;
  }
}

/**
 * Quick AES-only utility (optional)
 * ---------------------------------
 * Useful for local secure storage or ephemeral data.
 */
export const AES = {
  encrypt: (text, key, iv) => {
    const cipher = forge.cipher.createCipher("AES-CFB", key);
    cipher.start({ iv });
    cipher.update(forge.util.createBuffer(text, "utf8"));
    cipher.finish();
    return forge.util.encode64(cipher.output.getBytes());
  },
  decrypt: (encrypted, key, iv) => {
    const decipher = forge.cipher.createDecipher("AES-CFB", key);
    decipher.start({ iv });
    decipher.update(forge.util.createBuffer(forge.util.decode64(encrypted)));
    decipher.finish();
    return decipher.output.toString("utf8");
  },
};

// ========== LOCAL STORAGE ENCRYPTION UTILITIES ==========

const STORAGE_KEY = "cpgrams_secure_auth";
const ENCRYPTION_SECRET =
  import.meta.env.VITE_LOCAL_STORAGE_SECRET ||
  "cpgrams-default-secret-key-2024";

/**
 * Generate a consistent AES key and IV from the secret
 * @returns {Object} { key, iv }
 */
const generateKeyAndIV = () => {
  const hash = forge.md.sha256.create();
  hash.update(ENCRYPTION_SECRET);
  const key = hash.digest().getBytes(32); // 256-bit key

  const ivHash = forge.md.sha256.create();
  ivHash.update(ENCRYPTION_SECRET + "_iv");
  const iv = ivHash.digest().getBytes(16); // 128-bit IV

  return { key, iv };
};

/**
 * Encrypt auth data for localStorage
 * @param {Object} authData - Auth data object to encrypt
 * @returns {string|null} Encrypted string or null on error
 */
export const encryptAuthData = (authData) => {
  try {
    const { key, iv } = generateKeyAndIV();
    const jsonString = JSON.stringify(authData);
    const encrypted = AES.encrypt(jsonString, key, iv);
    return encrypted;
  } catch (error) {
    console.error("❌ Error encrypting auth data:", error);
    return null;
  }
};

/**
 * Decrypt auth data from localStorage
 * @param {string} encryptedData - Encrypted auth data string
 * @returns {Object|null} Decrypted auth object or null on error
 */
export const decryptAuthData = (encryptedData) => {
  try {
    if (!encryptedData) return null;

    const { key, iv } = generateKeyAndIV();
    const decryptedString = AES.decrypt(encryptedData, key, iv);
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error("❌ Error decrypting auth data:", error);
    return null;
  }
};

/**
 * Save encrypted auth data to localStorage
 * @param {Object} authData - Auth data object { user, token, timestamp }
 * @returns {boolean} Success status
 */
export const saveSecureAuth = (authData) => {
  try {
    const encrypted = encryptAuthData(authData);
    if (encrypted) {
      localStorage.setItem(STORAGE_KEY, encrypted);
      return true;
    }
    return false;
  } catch (error) {
    console.error("❌ Error saving secure auth:", error);
    return false;
  }
};

/**
 * Load and decrypt auth data from localStorage
 * @returns {Object|null} Decrypted auth object or null if not found
 */
export const loadSecureAuth = () => {
  try {
    const encrypted = localStorage.getItem(STORAGE_KEY);
    if (!encrypted) return null;

    const decrypted = decryptAuthData(encrypted);
    return decrypted;
  } catch (error) {
    console.error("❌ Error loading secure auth:", error);
    return null;
  }
};

/**
 * Clear auth data from localStorage
 * @returns {boolean} Success status
 */
export const clearSecureAuth = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);

    // Also clear old keys if they exist (migration cleanup)
    localStorage.removeItem("cpgrams_user");
    localStorage.removeItem("cpgrams_token");
    localStorage.removeItem("cpgrams_auth_timestamp");

    return true;
  } catch (error) {
    console.error("❌ Error clearing secure auth:", error);
    return false;
  }
};
